import sha1 from 'crypto-js/sha1'
import {
  ColumnDataType,
  Language,
  OutputNodeDefinition,
  Provider,
  StoredProcedureScope,
  TableSchema,
  WorkflowComponentParamType,
  WorkflowComponentValue,
  WorkflowComponentValueType,
  WorkflowParameterValueError,
  WorkflowParameterValueErrorType,
  WorkflowTableSchemas,
  WorkflowVariable
} from '../types'

export function hashString(s: string | undefined) {
  return sha1(s || '')
    ?.toString()
    ?.slice(0, 16)
}

export function stringToValidTableName(s: string) {
  return s?.replace(/[\s.-]/g, '_')
}

export function tempTableName(component: string, param: unknown) {
  return `__temp_${hashString(`${component}:${param}`)}`
}

/**
 * Type definition for join column configuration
 */
export type JoinColumnConfig = { name: string; joinname: string }

/**
 * Safely parses join column configuration from JSON string
 * @param jsonString - The JSON string containing column configuration
 * @returns Array of join column configurations, or empty array on failure
 */
export function parseJoinColumns(jsonString: string | null | undefined): JoinColumnConfig[] {
  try {
    if (jsonString) {
      return JSON.parse(jsonString) as JoinColumnConfig[]
    }
    return []
  } catch (e) {
    return []
  }
}

/**
 * Validates that at least one column is selected from either table in a join
 * @param leftColumns - JSON string of left table columns
 * @param rightColumns - JSON string of right table columns
 * @param errorParameterName - The parameter name to use in the error (e.g., 'lefttablecolumns' or 'maintablecolumns')
 * @returns WorkflowParameterValueError if validation fails, null if validation passes
 */
export function validateAtLeastOneColumnSelected(
  leftColumns: string | null | undefined,
  rightColumns: string | null | undefined,
  errorParameterName: string
): WorkflowParameterValueError | null {
  const parsedLeftColumns = parseJoinColumns(leftColumns)
  const parsedRightColumns = parseJoinColumns(rightColumns)

  const leftEmpty = parsedLeftColumns.length === 0
  const rightEmpty = parsedRightColumns.length === 0

  if (leftEmpty && rightEmpty) {
    return {
      parameter: errorParameterName,
      type: WorkflowParameterValueErrorType.MissingValue,
      message: 'At least one column must be selected from either table'
    }
  }

  return null
}

/**
 * Builds the output schema for a join operation by mapping selected columns from both tables
 * @param leftTableSchema - Schema of the left/main table
 * @param rightTableSchema - Schema of the right/secondary table
 * @param leftColumns - JSON string of left table column configurations
 * @param rightColumns - JSON string of right table column configurations
 * @returns Combined output schema with renamed columns
 */
export function buildJoinOutputSchema(
  leftTableSchema: TableSchema,
  rightTableSchema: TableSchema,
  leftColumns: string | null | undefined,
  rightColumns: string | null | undefined
): TableSchema {
  const parsedLeftColumns = parseJoinColumns(leftColumns)
  const parsedRightColumns = parseJoinColumns(rightColumns)

  const leftSchema: TableSchema = []
  const rightSchema: TableSchema = []

  if (parsedLeftColumns.length > 0) {
    for (const column of parsedLeftColumns) {
      const leftTableColumn = leftTableSchema.find((c) => c.name === column.name)
      if (leftTableColumn) {
        leftSchema.push({
          ...leftTableColumn,
          name: column.joinname
        })
      }
    }
  }

  if (parsedRightColumns.length > 0) {
    for (const column of parsedRightColumns) {
      const rightTableColumn = rightTableSchema.find((c) => c.name === column.name)
      if (rightTableColumn) {
        rightSchema.push({
          ...rightTableColumn,
          name: column.joinname
        })
      }
    }
  }

  return [...leftSchema, ...rightSchema]
}

/**
 * Builds a column list array for SQL SELECT clause in join operations
 * @param leftColumns - JSON string of left table column configurations
 * @param rightColumns - JSON string of right table column configurations
 * @param leftAlias - Table alias for left/main table (e.g., '_left' or '_main')
 * @param rightAlias - Table alias for right/secondary table (e.g., '_right' or '_joined')
 * @returns Array of aliased column strings for SELECT clause
 */
export function buildJoinColumnList(
  leftColumns: string | null | undefined,
  rightColumns: string | null | undefined,
  leftAlias: string,
  rightAlias: string
): string[] {
  const parsedLeftColumns = parseJoinColumns(leftColumns)
  const parsedRightColumns = parseJoinColumns(rightColumns)

  const cols: Array<string> = []

  if (parsedLeftColumns.length > 0) {
    cols.push(...parsedLeftColumns.map((x) => `${leftAlias}.${x.name} AS ${x.joinname}`))
  }

  if (parsedRightColumns.length > 0) {
    cols.push(...parsedRightColumns.map((x) => `${rightAlias}.${x.name} AS ${x.joinname}`))
  }

  return cols
}

/**
 * Validates that join key columns have matching types across both tables
 * @param schemas - Array of workflow table schemas
 * @param leftTableParam - Left/main table parameter value
 * @param rightTableParam - Right/secondary table parameter value
 * @param leftColumnName - Name of the key column in the left table
 * @param rightColumnName - Name of the key column in the right table
 * @param errorParameterName - The parameter name to use in the error
 * @returns WorkflowParameterValueError if validation fails, null if validation passes
 */
export function validateJoinKeyColumnTypes(
  schemas: WorkflowTableSchemas | undefined,
  leftTableParam: WorkflowComponentValue | undefined,
  rightTableParam: WorkflowComponentValue | undefined,
  leftColumnName: string | undefined,
  rightColumnName: string | undefined,
  errorParameterName: string
): WorkflowParameterValueError | null {
  if (!schemas || !leftColumnName || !rightColumnName) {
    return null
  }

  let firstType, secondType

  if (leftTableParam?.value && rightTableParam?.value) {
    const { component: leftComponent, output: leftOutput } = leftTableParam.value as OutputNodeDefinition
    const leftComponentSchemas = schemas.filter((x) => x.componentName === leftComponent)?.[0]
    if (leftComponentSchemas) {
      const tableSchema = leftComponentSchemas.schemas.filter((x) => x.name === leftOutput)?.[0]
      if (tableSchema && tableSchema.schema) {
        firstType = tableSchema.schema.find((x) => x.name === leftColumnName)?.type
      }
    }

    const { component: rightComponent, output: rightOutput } = rightTableParam.value as OutputNodeDefinition
    const rightComponentSchemas = schemas.filter((x) => x.componentName === rightComponent)?.[0]
    if (rightComponentSchemas) {
      const tableSchema = rightComponentSchemas.schemas.filter((x) => x.name === rightOutput)?.[0]
      if (tableSchema && tableSchema.schema) {
        secondType = tableSchema.schema.find((x) => x.name === rightColumnName)?.type
      }
    }

    if (firstType && secondType && firstType !== secondType) {
      return {
        parameter: errorParameterName,
        type: WorkflowParameterValueErrorType.WrongValue,
        message: 'Type of selected columns must be identical'
      }
    }
  }

  return null
}

export const WORKFLOW_OUTPUT_TABLE_NAME_PARAMETER_NAME = '_outputtablename'
export const OUTPUT_TABLE_NAME_VAR_NAME = '__outputtable'
export const OUTPUT_TABLE_FQN_VAR_NAME = '__outputtablefqn'

export const IF_OUTPUT_NAME = 'if'
export const ELSE_OUTPUT_NAME = 'else'

export function isExpressionValue(value: WorkflowComponentValue | string) {
  if (typeof value === 'string') {
    return /^\s*\{\{.*\}\}\s*$/.test(value)
  } else if (value && value.type === WorkflowComponentValueType.Literal && value.value) {
    return /^\s*\{\{.*\}\}\s*$/.test(`${value.value}`)
  } else {
    return false
  }
}

export function containsExpressionValue(value: WorkflowComponentValue | string) {
  if (typeof value === 'string') {
    return /\{\{.*\}\}/.test(value)
  } else if (value && value.type === WorkflowComponentValueType.Literal && value.value) {
    return /\{\{.*\}\}/.test(`${value.value}`)
  } else {
    return false
  }
}

export function isSingleVariable(value: WorkflowComponentValue) {
  if (value && value.type === WorkflowComponentValueType.Literal && value.value) {
    return /^\{\{@[a-zA-Z_$][a-zA-Z_$0-9]*\}\}$/.test(`${value.value}`)
  }
  return false
}

export function getVariablesUsedInQuery(query: string, variables: Array<WorkflowVariable>) {
  const regex = /@([a-zA-Z_$][a-zA-Z_$0-9]*)/g
  const matches = query.match(regex)
  let varNames: Array<string> = []
  if (matches) {
    varNames = matches.map((x) => x.replace('@', ''))
  }
  return (variables || []).filter((v) => varNames.includes(v.name)).map((v) => v.name)
}

export function formatVariableValue(variable: WorkflowVariable) {
  return variable.type === WorkflowComponentParamType.String
    ? `'${(variable.value as string)?.replace(/'/g, "\\'") || ''}'`
    : `${variable.value}`
}

export function sortByName(a, b) {
  if (a.name > b.name) return 1
  if (a.name < b.name) return -1
  return 0
}

const DATE_REGEX = /^\d{4}\.(0[1-9]|1[012])\.(0[1-9]|[12]\d|3[01])$/
const SEMVER_REGEX =
  /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-([0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*))?(?:\+[0-9A-Za-z-]+)?$/

export function checkGreaterOrEqualATVersion(minVersion, currentVersion) {
  /*
    Versioning has changed, from:
    - Date format: `yyyy.mm.dd` to
    - Semver format: `major.minor.patch`
    So for example 0.1.0 is a version newer than 2022.12.15
  */
  if (DATE_REGEX.test(minVersion)) {
    if (DATE_REGEX.test(currentVersion)) {
      // NOTE: Support for legacy format `yyyy.mm.dd`
      return currentVersion >= minVersion
    } else if (SEMVER_REGEX.test(currentVersion)) {
      return true
    }
  } else if (SEMVER_REGEX.test(minVersion)) {
    if (DATE_REGEX.test(currentVersion)) {
      return false
    } else if (SEMVER_REGEX.test(currentVersion)) {
      // NOTE: Current format `major.minor.patch`
      if (currentVersion === minVersion) {
        return true
      }
      const minNumbers = minVersion.split('.')
      const currentNumbers = currentVersion.split('.')
      for (const [minNumber, currentNumber] of minNumbers.map((n, i) => [Number(n), Number(currentNumbers[i])])) {
        if (currentNumber !== minNumber) {
          return currentNumber >= minNumber
        }
      }
    }
  }

  // Fallback: unknown version format
  return false
}

export function getValueTypeFromSchema(column: string, schema?: TableSchema) {
  let valueType
  if (schema) {
    valueType = schema.find((s) => s.name === column)?.type
  }
  return valueType
}

export function formatValue(value: string, connectionType: Provider, valueType?: string) {
  switch (valueType) {
    case ColumnDataType.String:
      return `'${value.replace(/\\/g, '\\\\').replace(/'/g, "\\'")}'`
    case ColumnDataType.Date:
    case ColumnDataType.Datetime:
    case ColumnDataType.Time:
    case ColumnDataType.Boolean:
    case ColumnDataType.Timestamp:
      switch (connectionType) {
        case Provider.BigQuery:
        case Provider.Redshift:
          return `CAST ('${value}' AS ${valueType})`
        case Provider.Snowflake:
          return `TO_${(valueType as string).toUpperCase()}('${value}')`
        case Provider.Postgres:
          return `'${value}'::${valueType}`
      }
      break
    default:
      // Unknown type, so it tries to detect string values
      if (isNaN(Number(value))) {
        return `'${value.replace(/\\/g, '\\\\').replace(/'/g, "\\'")}'`
      }
      return value
  }
}

export function getDBTypesByProvider(provider?: Provider): Record<string, unknown> {
  switch (provider) {
    case Provider.BigQuery:
      return {
        types: [
          'STRING',
          'NUMERIC',
          'BIGNUMERIC',
          'INT64',
          // 'INTERVAL',
          'FLOAT64',
          'BOOL',
          'DATE',
          'TIME',
          'DATETIME',
          'TIMESTAMP'
          // 'JSON',
          // 'BYTES',
        ]
      }
    case Provider.Snowflake:
      return {
        types: [
          'TEXT',
          'NUMBER',
          'FLOAT',
          'BOOLEAN',
          'DATE',
          'TIME',
          'TIMESTAMP'
          // 'TIMESTAMPTZ',
          // 'OBJECT',
          // 'ARRAY',
          // 'VARIANT',
          // 'BINARY',
        ]
      }
    case Provider.Redshift:
      return {
        types: [
          'CHAR',
          'VARCHAR',
          // 'INT2',
          'INT4',
          'INT8',
          'FLOAT4',
          'FLOAT8',
          'NUMERIC',
          'BOOL',
          'DATE',
          'TIME',
          // 'TIMETZ',
          'TIMESTAMP'
          // 'TIMESTAMPTZ',
          // 'VARBYTE',
        ]
      }
    case Provider.Postgres:
      return {
        types: [
          'CHAR',
          'VARCHAR',
          'TEXT',
          // 'INT2',
          'INT4',
          'INT8',
          // 'SERIAL2',
          // 'SERIAL4',
          // 'SERIAL8',
          'FLOAT4',
          'FLOAT8',
          'NUMERIC',
          'BOOL',
          'DATE',
          'TIME',
          // 'TIMETZ',
          'TIMESTAMP'
          // 'TIMESTAMPTZ',
          // 'JSON',
          // 'JSONB',
          // 'BYTEA',
        ]
      }
    case Provider.DatabricksRest:
      // Sub-set of types: https://docs.databricks.com/en/sql/language-manual/sql-ref-datatypes.html
      return {
        types: [
          'TINYINT',
          'SMALLINT',
          'INT',
          'BIGINT',
          'FLOAT',
          'DOUBLE',
          'DECIMAL',
          'DATE',
          'TIMESTAMP',
          'TIMESTAMP_NTZ',
          'STRING',
          'BOOLEAN'
        ]
      }
    default:
      return {}
  }
}

export function formatCodeComment(language: Language, s: string) {
  switch (language) {
    case Language.SQL:
      return `/*${s}*/\n`
    case Language.Python:
      return `# ${s}\n`
    default:
      return s
  }
}

export function nop(provider: Provider) {
  switch (provider) {
    case Provider.DatabricksRest:
      return 'pass'
    case Provider.Postgres:
      return 'PERFORM 1;'
    default:
      return 'SELECT 1;'
  }
}
